<template>
    <v-row no-gutters class="add-comment">
        <v-col>
            <footer-titlebar
                v-if="!messagingAvailable"
                icon="mdi-comment-outline"
                :title="$t('Add comment')"
                @close="$emit('close')"
            />
            <footer-titlebar
                v-else
                title=""
                reduced-spacing
                @close="$emit('close')"
            >
                {{action}}
                <template #default>
                    <v-btn
                        :primary="action === 'comment'"
                        depressed
                        x-small
                        style="border-bottom-left-radius: 0; border-bottom-right-radius: 0; padding-top: 18px; padding-bottom: 20px"
                        :class="['ml-2 px-3 title-button', action==='comment'?'white':'n4 white--text']"
                        @click="$emit('update-action', 'add_comment')"
                    >
                        <icon
                            size="20"
                            icon="MessageSquare"
                            :color="action === 'comment' ? 'n3' : 'white'"
                        />
                        <div class="ml-2 subtitle-2 text-none">
                            {{ $t('Internal comment') }}
                        </div>
                    </v-btn>
                    <v-btn
                        :primary="action !== 'comment'"
                        depressed
                        x-small
                        :class="['ml-2 px-3 title-button', action!=='comment'?'white':'n4 white--text']"
                        style="border-bottom-left-radius: 0; border-bottom-right-radius: 0; padding-top: 18px; padding-bottom: 20px"
                        @click="$emit('update-action', 'add_message')"
                    >
                        <icon
                            size="20"
                            icon="Users"
                            :color="action !== 'comment' ? 'n3' : 'white'"
                        />
                        <div class="ml-2 subtitle-2 text-none">
                            {{ $t('Portal message') }}
                        </div>
                        <v-tooltip bottom max-width="250">
                            <template v-slot:activator="{ on }">
                                <div v-on="on">
                                    <icon icon="Info" small class="ml-2"/>
                                </div>
                            </template>
                            <span>{{ $t('Public message means that clients can read it and the client portal will notify them by email') }}</span>
                        </v-tooltip>
                    </v-btn>
                </template>
            </footer-titlebar>
        </v-col>
        <div class="ml-9 mr-3 mb-2" style="width: 100%; position: relative">
            <v-col class="col-12 pa-0 pt-2">
                <v-row no-gutters>
                    <v-col class="pt-0">
                        <v-textarea
                            ref="commentField"
                            v-model="form_data.message"
                            :placeholder="$t('Type a comment')"
                            class="pt-0 body-2 no-t-radius white-bg"
                            rows="3"
                            auto-grow
                            outlined
                            dense
                            :hide-details="!form_data.message_error"
                            :error-messages="form_data.message_error"
                        ></v-textarea>
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col class="py-0">
                        <div style="display: initial; position: absolute; opacity: 0; width: 32px;">
                            <input
                                id="userfile736"
                                type="file"
                                multiple
                                name="userfile736"
                                style="width: 32px;"
                                @change="$emit('upload_change', $event)"
                            />
                        </div>
                        <label
                            for="userfile736"
                            class="v-btn v-btn--flat v-btn--icon v-btn--round theme--light v-size--default"
                            style="cursor: pointer;"
                        >
                            <v-icon>mdi-paperclip</v-icon>
                        </label>
                    </v-col>
                    <v-col class="py-0 shrink">
                        <v-btn
                            color="primary"
                            class="inverted--text"
                            :loading="form_data.message_loading"
                            @click.stop="save_comment"
                        >
                            {{ action === 'message' ? $t("Send portal message") : $t('Add comment') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-col>
        </div>
    </v-row>
</template>

<script>
import { ref, computed, watch } from "@vue/composition-api";
import AccountModule from "../../store/modules/account";
import AppModule from "../../store/modules/app";
import TaskModule from "../../store/modules/task";
import PersonAvatar from "../../components/PersonAvatar.vue";
import FooterTitlebar from "./FooterTitlebar"
import {getDescriptionWithLinks} from "@/helpers/formatting";

export default {
    name: "TaskAddCommentControl",
    components: {
        PersonAvatar,
        FooterTitlebar
    },
    props: {
        taskData: {
            type: Object,
            required: true
        },
        actionType: {
            type: String,
            required: true
        },
        relatedAttachments: {
            type: Array,
            required: true
        },
        clientName: {
            type: String,
            required: true
        },
        users: {
            type: Array,
            required: true
        },
        messagingAvailable: {
            type: Boolean,
            required: true
        }
    },
    setup(props, ctx) {
        const appModule = AppModule();
        const { current_account } = AccountModule();
        const { saveTaskComment } = TaskModule();

        const commentField = ref(null);
        const form_data = ref({
            message: "",
            message_error: null,
            message_loading: false
        });

        const attachments = computed(function() {
            return props.relatedAttachments;
        });
        const selectedAccounts = computed(function() {
            if (!props.taskData.cp_assignees) return [];
            return props.users.filter(x => props.taskData.cp_assignees.indexOf(x.id) >= 0);
        });

        const action = computed(() => {
            return props.messagingAvailable && props.actionType === 'add_message' ? 'message' : 'comment'
        })

        const save_comment = function() {
            form_data.value.message_loading = true;

            let data = {
                id: props.taskData.id,
                virtual_id: props.taskData.virtual_id,
                message: getDescriptionWithLinks(form_data.value.message),
                action: action.value
            };

            saveTaskComment(data)
                .then(() => {
                    form_data.value.message_loading = false;
                    ctx.emit("close");
                    if(data.action === 'message') ctx.emit('message_sent')
                })
                .catch((ex) => {
                    appModule.handleErrorResponse(form_data.value, ex);
                    form_data.value.message_loading = false;
                });
        };

        watch(attachments, function(val) {
            for (let i = 0; i < val.length; i++) {
                let postfix = "[File:{0}|{1}]".format(val[i].id, val[i].file_name);
                form_data.value.message =
                    form_data.value.message == null ? postfix : form_data.value.message + " " + postfix;
            }
        });

        setTimeout(function() {
            commentField.value.focus();
        }, 100);

        return {
            commentField,
            form_data,
            current_account,
            selectedAccounts,
            save_comment,
            action
        };
    }
};
</script>
<style scoped lang="scss">
.v-text-field {
    ::v-deep .v-input__control {
        .v-text-field__slot {
            margin-right: 0 !important;
        }
    }
}

.title-button {
    .v-btn__content {
        text-transform: none;
    }
}
</style>